import Styles from './WizardButton.module.scss'
import cx from 'classnames'

import { NewDocumentButtonProps, WizardButtonContentProps, WizardButtonProps } from './Types'
import { TextChannelKey } from 'types'
import { observer } from 'mobx-react-lite'
import { Text } from 'blaze-ui/Text/Text'
import { PricePill } from 'blaze-ui/Pricing/PricePill/PricePill'
import { ContentType } from 'components/ContentType/ContentType'
import { useState } from 'react'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { NewFileModal } from 'components/NewFileModal/NewFileModal'
import { useModals } from 'almanac-ui/Modal'
import { reportError } from 'utils/error'
import { useNeedsUpgradeForChannelType } from 'hooks/useChannelNeedsUpgrade'
import { Heading } from 'blaze-ui/Heading/Heading'
import { WIZARD_CARD_INFO } from '../constants'
import { V2Channels, WizardCardInfo } from '../Types'
import { getChannelInfo } from 'utils/wizards'
import { Design, File02 } from 'blaze-ui/icons'
import { Pressable } from '@react-aria/interactions'
import { usePaywallGuard } from 'hooks/usePaywallGuard'

export const WizardCard = ({
  onPress,
  ref,
  size = 'lg',
  image,
  icon,
  badge,
  title,
  description,
  headerPadding = true,
}: WizardButtonContentProps) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const guardedOnPress = usePaywallGuard(onPress)

  return (
    <Pressable onPress={guardedOnPress}>
      <div
        className={cx(Styles.wizardCard, Styles[size])}
        ref={ref}
        style={{
          backgroundImage: isLoaded && image ? `url(${image})` : undefined,
        }}
      >
        <div className={cx(Styles.textContainer, { [Styles.headerPadding]: headerPadding })}>
          <Heading level={4}>{title}</Heading>
        </div>
        <div className={Styles.description}>
          <Text variant="secondary">{description}</Text>
        </div>
        <div className={Styles.iconContainer}>{icon}</div>
        <div className={Styles.imageContainer}>
          {image && (
            <>
              {!isLoaded && <LoadingShimmerer className={Styles.shimmerer} />}
              <img src={image} alt="" style={{ display: 'none' }} onLoad={() => setIsLoaded(true)} />
            </>
          )}
          {badge}
        </div>
      </div>
    </Pressable>
  )
}

export const WizardButtonContent = ({ onPress, ref, size = 'md', image, badge, title }: WizardButtonContentProps) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const guardedOnPress = usePaywallGuard(onPress)

  return (
    <div className={cx(Styles.wizardButton, Styles[size])} onClick={guardedOnPress as () => {}} ref={ref}>
      <div className={Styles.imageContainer}>
        {image && (
          <>
            {!isLoaded && <LoadingShimmerer className={Styles.shimmerer} />}
            <img
              src={image}
              alt=""
              className={Styles.image}
              onLoad={() => setIsLoaded(true)}
              style={{ display: isLoaded ? 'block' : 'none' }}
            />
          </>
        )}
        {badge}
      </div>
      <div className={Styles.textContainer}>
        <Text variant="smallList" lineClamp={2}>
          {title}
        </Text>
      </div>
    </div>
  )
}

export const NewDocumentButton = ({ size, onSuccess, title, image }: NewDocumentButtonProps) => {
  const { openModal } = useModals()
  const guardedOnPress = usePaywallGuard(() => openModal(NewFileModal, { onSuccess }))

  return (
    <WizardButtonContent
      onPress={guardedOnPress}
      size={size || 'md'}
      title={title || 'Blank Doc'}
      image={
        image ||
        'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Blank_doc_npksy7.png'
      }
    />
  )
}

export const NewDesignButton = ({ size, onSuccess, title, image, initialAspectRatioKey }: NewDocumentButtonProps) => {
  const { openModal } = useModals()
  const guardedOnPress = usePaywallGuard(() =>
    openModal(NewFileModal, { onSuccess, initialFileType: 'design', initialAspectRatioKey }),
  )

  return (
    <WizardButtonContent
      onPress={guardedOnPress}
      size={size || 'md'}
      title={title || 'Blank Design'}
      image={
        image ||
        'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_blank_design_jrsvpd.png'
      }
    />
  )
}

export const NewDocumentButtonV2 = ({ size, onSuccess, title, image, icon }: NewDocumentButtonProps) => {
  const { openModal } = useModals()
  const guardedOnPress = usePaywallGuard(() => openModal(NewFileModal, { onSuccess }))

  return (
    <WizardCard
      onPress={guardedOnPress}
      size={size || 'lg'}
      title={title || 'Blank Doc'}
      icon={icon || <File02 color="var(--dark-40)" size={24} />}
      image={
        image ||
        'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/blank_doc_h0zncz.png'
      }
    />
  )
}

export const NewDesignButtonV2 = ({
  size,
  onSuccess,
  title,
  image,
  icon,
  initialAspectRatioKey,
}: NewDocumentButtonProps) => {
  const { openModal } = useModals()
  const guardedOnPress = usePaywallGuard(() =>
    openModal(NewFileModal, { onSuccess, initialFileType: 'design', initialAspectRatioKey }),
  )

  return (
    <WizardCard
      onPress={guardedOnPress}
      size={size || 'lg'}
      title={title || 'Blank Design'}
      icon={icon || <Design color="var(--dark-40)" size={24} />}
      image={
        image ||
        'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/v2_card_images/blank_doc_h0zncz.png'
      }
    />
  )
}

export const WizardButton = observer(
  ({
    channelKey,
    initialValues,
    onSuccess,
    image,
    title,
    description,
    disabled = false,
    size = 'md',
    schemaModifier,
    initialFormStatus,
    version = 1,
    headerPadding = true,
    cardInfoOverride = {},
  }: WizardButtonProps) => {
    const { name, icon: Icon, image: channelImage } = getChannelInfo(channelKey)

    const v2Item = WIZARD_CARD_INFO[channelKey as V2Channels]

    if (!name) {
      reportError('Invalid wizard channel being used in create new', {
        metaData: { channel: channelKey },
      })

      return <></>
    }

    let item: WizardCardInfo = {
      title: name,
      icon: <Icon size={24} />,
      image: channelImage,
    }

    if (version === 2 && v2Item) {
      item = { ...v2Item, ...(cardInfoOverride[channelKey] ?? {}) }
    }

    const finalImage = image || item.image

    const { shouldForceUpgrade, nextTierPlan } = useNeedsUpgradeForChannelType()

    const Component = version === 2 ? WizardCard : WizardButtonContent

    return (
      <ContentType
        type={channelKey}
        key={channelKey}
        schemaModifier={schemaModifier}
        initialFormStatus={initialFormStatus}
        initialValues={initialValues}
        onSuccess={onSuccess}
        isDisabled={disabled}
      >
        <Component
          key={item.title}
          onPress={() => {}}
          size={size}
          image={finalImage}
          badge={
            shouldForceUpgrade(channelKey as TextChannelKey) && nextTierPlan ? (
              <div className={Styles.upgradeBadge} data-testid="upgrade-badge-container">
                <PricePill plan={nextTierPlan.key} />
              </div>
            ) : (
              <></>
            )
          }
          icon={item.icon}
          title={title || item.title}
          description={description}
          headerPadding={headerPadding}
        />
      </ContentType>
    )
  },
)
